import styled from '@emotion/styled'
import { Command } from 'cmdk'
import { ReactNode } from 'react'

import { Enumerable, Truthy, isTruthy, toArray } from '@bemlo/utils'

import { Color, theme } from '../../../constants'
import { useTranslations } from '../../../translate'
import { Avatar } from '../../Avatar'
import { Badge, BadgeColor } from '../../Badge'
import { Checkbox } from '../../Checkbox'
import { Div } from '../../Div'
import { Flex } from '../../Flex'
import { Icon, IconName } from '../../Icon'
import { SelectedCheckmark } from '../SelectedCheckmark'

type OptionType<T extends string | number> = {
  badgeColor?: BadgeColor
  icon?: IconName
  label: string
  user?: { avatarUrl?: string; name: string }
  value: T
}

type OptionGroup<T extends string | number> = {
  items: OptionType<T>[]
  label?: string
}

const Options = styled(Command.List)({
  borderTop: theme.border.style.thinLight,
  maxHeight: 400,
  overflow: 'auto',
  overscrollBehavior: 'contain',
})

const Group = styled(Command.Group, {
  shouldForwardProp: (prop) => prop !== 'hasGroupLabels',
})<{ hasGroupLabels: boolean }>`
  :not(:first-of-type) {
    border-top: ${({ hasGroupLabels }) =>
      hasGroupLabels ? 'none' : theme.border.style.thinLight};
  }

  [cmdk-group-heading] {
    user-select: none;
    font-size: 12px;
    color: ${Color.DARKEST_MEDIUM_GREY};
    padding: 8px 16px;
    display: flex;
    align-items: center;
    font-weight: ${theme.font.weight.bold};
  }
`

const Option = styled(Command.Item)<{ selected: boolean }>`
  font: inherit;
  font-size: 14px;
  gap: 12px;
  padding: 8px;
  padding: ${theme.padding.button.small.y}px ${theme.padding.button.small.x}px;
  overflow: hidden;
  user-select: none;
  color: ${Color.BLACK};
  cursor: pointer;
  /* Hide checkbox unless selected or highlighted */
  input {
    display: ${({ selected }) => (selected ? 'block' : 'none')};
  }
  &[data-selected='true'] {
    background: ${Color.LIGHTEST_BLUE};
    color: ${Color.CHARCOAL};
    /* Show checkbox if highlighted */
    input {
      display: block;
    }
    svg {
      color: ${Color.DARKER_GREY};
    }
  }
  &[data-disabled='true'] {
    /* TODO: cannot disable yet - improve styling */
    cursor: not-allowed;
  }
  &:active {
    transition-property: background;
    background: ${Color.DARK_MEDIUM_GREY};
  }
  svg {
    color: ${Color.DARK_GREY};
  }
`

const OptionContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  p: {
    margin: 0,
  },
})

const NoItemsFound = styled(Command.Empty)({
  padding: `${theme.padding.button.medium.y}px ${theme.padding.button.medium.x}px`,
  color: Color.DARK_GREY,
  fontSize: 14,
  fontWeight: theme.font.weight.legacy[500],
  textAlign: 'center',
})

export type PopoverComboboxOptions<T extends string | number> = {
  actionsSlot?: ReactNode
  groups: Enumerable<OptionGroup<T>>
  noItemsFoundLabel?: string
  onSelect: (value: T) => void
  type: 'multiSelect' | 'singleSelect' | 'trigger'
  value?: Enumerable<T> | null | undefined
}

export const PopoverComboboxOptions = <T extends string | number>({
  actionsSlot,
  groups,
  noItemsFoundLabel,
  onSelect,
  type,
  value,
}: PopoverComboboxOptions<T>) => {
  const t = useTranslations()

  const optionsGroups = toArray(groups)
  const selectedOptions = toArray(value).filter(isTruthy)
  const isMultiSelect = type === 'multiSelect'
  const isSingleSelect = type === 'singleSelect'

  const hasGroupLabels = optionsGroups.some((group) => group.label)
  const hasUser = optionsGroups.some((group) =>
    group.items.some((item) => item.user),
  )

  return (
    <Options>
      <NoItemsFound>
        {noItemsFoundLabel ?? t.form.no_alternatives()}
      </NoItemsFound>
      {optionsGroups.map((group, index) => (
        <Group
          key={index}
          heading={group.label}
          hasGroupLabels={hasGroupLabels}
        >
          {group.items.map(({ icon, label, value, user, badgeColor }) => {
            const isSelected = selectedOptions.includes(value as Truthy<T>)

            return (
              <Option
                key={value}
                value={label}
                onSelect={() => onSelect(value)}
                selected={isSelected}
              >
                <OptionContent>
                  {isMultiSelect && (
                    <Checkbox
                      checked={isSelected}
                      size="sm"
                      variant="inverted"
                    />
                  )}
                  {icon && (
                    <Flex
                      styles={{
                        height: hasUser ? 24 : 16,
                        width: hasUser ? 24 : 16,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name={icon} size="sm" />
                    </Flex>
                  )}
                  {user && (
                    <Avatar
                      name={user.name}
                      image={user.avatarUrl}
                      type={user.avatarUrl ? 'image' : 'initials'}
                      size="xs"
                    />
                  )}
                  {badgeColor ? (
                    <Badge color={badgeColor} size="sm">
                      {label}
                    </Badge>
                  ) : (
                    <p>{label}</p>
                  )}
                  {isSingleSelect && (
                    <SelectedCheckmark selected={isSelected} />
                  )}
                </OptionContent>
              </Option>
            )
          })}
        </Group>
      ))}
      {actionsSlot && (
        <Div styles={{ borderTop: theme.border.style.thin, p: 2 }}>
          {actionsSlot}
        </Div>
      )}
    </Options>
  )
}
